export const STAGES = [
  { id: "new_lead", label: "Nouveau lead", short: "Lead" },
  { id: "contacted", label: "Contacté", short: "Contacté" },
  { id: "meeting_scheduled", label: "RDV planifié", short: "RDV" },
  { id: "qualified", label: "Qualifié", short: "Qualifié" },
  { id: "demo_negotiation", label: "Démo / Négo", short: "Démo" },
  { id: "won", label: "Gagné", short: "Gagné" },
  { id: "onboarding_in_progress", label: "Onboarding", short: "Onboarding" },
  { id: "active_client", label: "Client actif", short: "Actif" },
  { id: "lost", label: "Perdu", short: "Perdu" },
];

// Chart fills (recharts needs plain colours). Same scale as the `.stage-*` chips in index.css: change both together.
export const STAGE_CHART_COLORS = {
  new_lead: "#B3D2E1",
  contacted: "#7FB3CC",
  meeting_scheduled: "#4B8FAE",
  qualified: "#2E7A99",
  demo_negotiation: "#166685",
  won: "#0F4C68",
  onboarding_in_progress: "#0A3D55",
  active_client: "#083346",
  lost: "#ef4444",
};

export const STAGE_LABEL = Object.fromEntries(STAGES.map((s) => [s.id, s.label]));

// "onboarding_in_progress" and "active_client" are reached only through their
// own dedicated actions (POST /prospects/{id}/start-onboarding, and the
// roadmap auto-completing a prospect in backend/prospects/routes.py), which
// create/advance the Onboarding object alongside the status. Letting a manual
// "changer le statut" control (dropdown or kanban drag) set them directly
// would leave status=onboarding_in_progress with no Onboarding doc behind it.
export const SYSTEM_MANAGED_STAGES = ["onboarding_in_progress", "active_client"];
export const MANUAL_STAGES = STAGES.filter((s) => !SYSTEM_MANAGED_STAGES.includes(s.id));

export const STAGE_GROUPS = [
  {
    id: "acquisition",
    label: "Acquisition",
    stageIds: ["new_lead", "contacted", "meeting_scheduled", "qualified", "demo_negotiation"],
  },
  {
    id: "conversion",
    label: "Conversion & activation",
    stageIds: ["won", "onboarding_in_progress", "active_client"],
  },
  {
    id: "exit",
    label: "Sortie",
    stageIds: ["lost"],
  },
];

// Columns hidden by default so a new pipeline view starts focused on active deals.
export const DEFAULT_HIDDEN_STAGES = ["won", "active_client", "lost"];

export const LOSS_REASONS = [
  "Prix",
  "Concurrent",
  "Pas de besoin",
  "Injoignable",
  "Timing",
  "Autre",
];

// "Canal d'acquisition" ("source du lead"), distinct from PROSPECT_SOURCE_LABELS
// below, which describes *how the prospect entered the CRM* (form sync/manual/
// csv), not *which marketing channel* brought them in. A prospect can have
// several at once, stored as a plain comma-separated string on
// `acquisition_channel` (split/join on ",").
export const ACQUISITION_CHANNELS = [
  { id: "google_form", label: "Google Form" },
  { id: "site_web", label: "Site web" },
  { id: "reseaux_sociaux", label: "Réseaux sociaux" },
  { id: "salon", label: "Salon" },
  { id: "recommandation", label: "Recommandation" },
  { id: "bouche_a_oreille", label: "Bouche-à-oreille" },
  { id: "appel_entrant", label: "Appel entrant" },
  { id: "autre", label: "Autre" },
];

export const ACQUISITION_CHANNEL_LABEL = Object.fromEntries(
  ACQUISITION_CHANNELS.map((c) => [c.id, c.label])
);

export function parseAcquisitionChannels(value) {
  if (!value) return [];
  return value.split(",").map((s) => s.trim()).filter(Boolean);
}

export const DATA_SOURCES = [
  { id: "onepharma", label: "Export natif + API Onepharma" },
  { id: "other_software", label: "Export d'un autre logiciel" },
  { id: "physical_inventory", label: "Inventaire physique" },
];

// Mirrors backend/interactions/models.py INTERACTION_TYPES exactly.
export const INTERACTION_TYPES = [
  { id: "note", label: "Note" },
  { id: "call", label: "Appel" },
  { id: "whatsapp", label: "WhatsApp" },
  { id: "email", label: "Email" },
  { id: "meeting", label: "Rendez-vous" },
  { id: "demo", label: "Démonstration" },
];

export const INTERACTION_LABELS = Object.fromEntries(INTERACTION_TYPES.map((t) => [t.id, t.label]));

// Mirrors backend prospect.source values (backend/prospects/routes.py,
// backend/server.py): "form" (Google Sheet sync), "manual" (internal
// creation), "csv_import".
export const PROSPECT_SOURCE_LABELS = {
  form: "Formulaire",
  manual: "Création manuelle",
  csv_import: "Import CSV",
};

export function prospectSourceLabel(source) {
  return PROSPECT_SOURCE_LABELS[source] || source || "Inconnue";
}

// Shared "time ago" formatting for activity/last-login-style timestamps.
export function formatRelativeDate(iso) {
  if (!iso) return "Jamais";
  const date = new Date(iso);
  const now = new Date();
  const diffMin = Math.floor((now - date) / 60000);
  if (diffMin < 1) return "À l'instant";
  if (diffMin < 60) return `Il y a ${diffMin} min`;
  if (date.toDateString() === now.toDateString()) {
    return date.toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" });
  }
  // Calendar-day gap, not elapsed hours: an event from last night is 1
  // calendar day away even if under 24h ago, so it must read "Hier", never
  // "Il y a 0 jour" (a raw ms/86400000 diff floors to 0 in that case).
  const startOfDay = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate());
  const dayDiff = Math.round((startOfDay(now) - startOfDay(date)) / 86400000);
  if (dayDiff === 1) return "Hier";
  if (dayDiff < 7) return `Il y a ${dayDiff} jours`;
  return date.toLocaleDateString("fr-FR");
}
